/**
 * @file LabelPrintItemsList.tsx
 * @description Строки диалога печати, сгруппированные по шаблону этикетки
 *   (phase19.22, W17-5b: каждый шаблон — свой лист; ui-design §1).
 *
 * Порядок внутри группы — порядок строк документа (так же идут этикетки в
 * PDF). Над списком — «Выбрать все N»; на длинной приёмке (20+ строк) —
 * быстрые отборы «Все / Не напечатаны / Устарели»: они только прячут строки
 * из вида, отметки не трогают.
 */

import { useState } from "react";

import { LabelPrintItemRow } from "./LabelPrintItemRow";
import type { LabelPrintForm, LabelRow } from "./useLabelPrintForm";
import { Checkbox } from "../../ui/checkbox";
import { SectionLabel } from "../../ui/SectionLabel";
import { LABEL_TEMPLATE_META, LABEL_TEMPLATE_ORDER } from "../../../lib/api-labels";
import { cn } from "@/lib/utils";

/** Быстрые отборы длинного списка — данными. */
type RowFilter = "all" | "todo" | "stale";

const FILTERS: { value: RowFilter; label: string; test: (row: LabelRow) => boolean }[] = [
  { value: "all", label: "Все", test: () => true },
  { value: "todo", label: "Не напечатаны", test: (r) => r.item.labelStatus !== "PRINTED" },
  { value: "stale", label: "Устарели", test: (r) => r.item.labelStatus === "STALE" },
];

/** С какого числа строк показывать быстрые отборы. */
const FILTERS_FROM = 20;

interface Props {
  form: LabelPrintForm;
}

export function LabelPrintItemsList({ form }: Props) {
  const [filter, setFilter] = useState<RowFilter>("all");
  const test = FILTERS.find((f) => f.value === filter)?.test ?? (() => true);
  const visible = form.rows.filter(test);
  const allChecked = form.rows.length > 0 && form.rows.every((r) => r.checked);
  const groups = LABEL_TEMPLATE_ORDER.map((template) => ({
    template,
    rows: visible.filter((r) => form.templateOf(r) === template),
  })).filter((g) => g.rows.length > 0);

  return (
    <div className="flex flex-col gap-3">
      {form.rows.length > 1 ? (
        <label className="flex min-h-14 cursor-pointer items-center gap-3 rounded-xl px-2">
          <span className="inline-flex size-10 items-center justify-center">
            <Checkbox
              checked={allChecked}
              onCheckedChange={() => form.setAll(!allChecked)}
              aria-label="Выбрать все строки"
              className="size-5"
            />
          </span>
          <span className="text-sm font-medium">Выбрать все {form.rows.length}</span>
        </label>
      ) : null}

      {form.rows.length >= FILTERS_FROM ? (
        <div className="flex gap-1.5 overflow-x-auto">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              aria-pressed={filter === f.value}
              onClick={() => setFilter(f.value)}
              className={cn(
                "min-h-12 shrink-0 rounded-full px-4 text-sm transition-colors",
                filter === f.value
                  ? "bg-primary font-medium text-on-primary"
                  : "bg-surface-low text-on-surface-variant hover:bg-accent",
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      ) : null}

      {groups.map((group) => {
        const meta = LABEL_TEMPLATE_META[group.template];
        return (
          <section key={group.template} className="flex flex-col gap-2">
            <SectionLabel as="div">
              {meta.title} (лист {meta.cols}×{meta.rows})
            </SectionLabel>
            <ul className="flex flex-col gap-2">
              {group.rows.map((row) => (
                <LabelPrintItemRow
                  key={row.key}
                  row={row}
                  baseTemplate={form.templateOf({ ...row, override: null })}
                  onToggle={() => form.toggle(row.key)}
                  onQty={(qty) => form.setQty(row.key, qty)}
                  onOverride={(t) => form.setOverride(row.key, t)}
                />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
